/* ============================================================================
 * brand-promo-slider.js  —  client for sections/brand-promo-slider.liquid.
 *
 * HC - Brand promo slider: brand-page carousel of the monthly promo feed - 2026-09-23
 *
 * Browser-global IIFE (window.IBBrandPromoSlider). No build step: raw, ES5-ish.
 * Loaded deferred by the section, AFTER promotions-feed.js (window.IBPromoFeed)
 * and shoppable-promotions.js (window.ShoppablePromotions), whose createEmbed()
 * supplies the promo-page cards AND the same product / collection modals. This
 * file only owns: feed fetch, the brand filter, the visibility rule, and the rail.
 *
 * Visibility rule (Noah, 2026-09-23):
 *   - region-restricted promos are dropped for everyone (preparePromos);
 *   - logged OUT  -> denied promos stay as LOCKED cards (Log in / Contact us);
 *   - logged IN   -> denied promos are dropped (no dead-end cards).
 * No matching promos -> the section stays `hidden` (theme editor shows a notice).
 * ==========================================================================*/

window.IBBrandPromoSlider = window.IBBrandPromoSlider || (function () {
  'use strict';

  var feedCache = Object.create(null);

  // "L'Oréal Professionnel" -> "lorealprofessionnel", "milk_shake" -> "milkshake",
  // "kevin-murphy" -> "kevinmurphy". Feed brands are free display strings, so
  // both sides are reduced to bare lowercase ASCII letters/digits.
  function normalizeBrand(value) {
    var s = String(value || '').toLowerCase();
    if (typeof s.normalize === 'function') s = s.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    return s.replace(/[^a-z0-9]/g, '');
  }

  // A promo belongs to the page when its brand EQUALS a candidate or STARTS WITH
  // one ("balmain" -> "Balmain Paris", "megix" -> "Megix10"). Prefix matching
  // needs 3+ characters so a stray short candidate can't sweep in other brands.
  function brandMatches(brand, candidates) {
    var b = normalizeBrand(brand);
    if (!b) return false;
    for (var i = 0; i < candidates.length; i++) {
      var c = candidates[i];
      if (!c) continue;
      if (b === c) return true;
      if (c.length >= 3 && b.indexOf(c) === 0) return true;
    }
    return false;
  }

  // Brand keys are the feed's stable brand ids ("kevin-murphy"), compared exactly.
  function normalizeKeys(list) {
    var out = [];
    (Array.isArray(list) ? list : []).forEach(function (item) {
      var k = String(item || '').trim().toLowerCase();
      if (k && out.indexOf(k) === -1) out.push(k);
    });
    return out;
  }

  // A promo belongs to the page when its brandKey is one of the section's keys.
  // Feeds without keys, or a section with none set, fall back to the display-name
  // match (brandMatches).
  function promoMatches(promo, keys, candidates) {
    if (keys.length && promo.brandKey) return keys.indexOf(promo.brandKey) !== -1;
    return brandMatches(promo.brand, candidates);
  }

  function normalizeCandidates(list) {
    var out = [];
    (Array.isArray(list) ? list : []).forEach(function (item) {
      var c = normalizeBrand(item);
      if (c && out.indexOf(c) === -1) out.push(c);
    });
    return out;
  }

  // Normalized feed promos -> the entries this page shows, in featured order.
  function selectEntries(promos, cfg, embed) {
    var keys = normalizeKeys(cfg.brandKeys);
    var candidates = normalizeCandidates(cfg.brandCandidates);
    if (!keys.length && !candidates.length) return [];
    var mine = (promos || []).filter(function (p) { return promoMatches(p, keys, candidates); });
    var loggedIn = !!(cfg.customer && cfg.customer.isLoggedIn);
    return embed.prepare(mine).filter(function (entry) {
      return !(loggedIn && entry.access && entry.access.allowed === false);
    });
  }

  // The engine's own feed fetch (createEmbed().fetchFeed), shared by every slider
  // on the page that reads the same feed.
  function fetchFeed(url, embed) {
    if (!feedCache[url]) {
      feedCache[url] = embed.fetchFeed();
      // A failed fetch must not poison later sliders / theme-editor reloads.
      feedCache[url].catch(function () { delete feedCache[url]; });
    }
    return feedCache[url];
  }

  function readConfig(root) {
    var node = root.querySelector('script[data-bps-config]');
    if (!node) return null;
    try { return JSON.parse(node.textContent); } catch (e) { return null; }
  }

  function isDesignMode() {
    return !!(window.Shopify && window.Shopify.designMode);
  }

  function reduceMotion() {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  // The section loads shoppable-promotions.css without blocking the page
  // (media="print", switched to "all" by its onload). Cards render once it has
  // applied, so they never flash unstyled. `sheet` covers a load that finished
  // without the inline handler running.
  function stylesReady(root) {
    var link = root.querySelector('link[data-bps-css]');
    if (!link) return Promise.resolve();
    if (link.media === 'all' || link.sheet) {
      link.media = 'all';
      return Promise.resolve();
    }
    return new Promise(function (resolve) {
      function done() { link.media = 'all'; resolve(); }
      link.addEventListener('load', done);
      link.addEventListener('error', done);
    });
  }

  // Nothing to show: stay hidden for shoppers; explain why in the theme editor.
  function showEmpty(root, message) {
    var notice = root.querySelector('[data-bps-notice]');
    var body = root.querySelector('[data-bps-body]');
    root.removeAttribute('aria-busy');
    if (isDesignMode() && notice) {
      notice.textContent = message;
      notice.hidden = false;
      if (body) body.hidden = true;
      root.hidden = false;
      return;
    }
    root.hidden = true;
  }

  function Slider(root) {
    this.root = root;
    this.entries = [];
    this.rail = root.querySelector('[data-bps-rail]');
    this.nav = root.querySelector('[data-bps-nav]');
    this.prevBtn = root.querySelector('[data-bps-prev]');
    this.nextBtn = root.querySelector('[data-bps-next]');
    this.cleanup = [];
  }

  Slider.prototype.init = function () {
    var self = this;
    var root = this.root;
    var cfg = readConfig(root);
    var SP = window.ShoppablePromotions;
    var Feed = window.IBPromoFeed;
    if (!cfg || !this.rail || !SP || typeof SP.createEmbed !== 'function' ||
        !Feed || typeof Feed.normalizeFeed !== 'function') {
      showEmpty(root, 'Brand promotions could not start: the promotions scripts did not load.');
      return;
    }
    this.cfg = cfg;
    var label = cfg.brandLabel || 'this brand';
    if (!cfg.feedUrl) {
      showEmpty(root, 'No promotions feed is selected. Set "Brand page promotions feed" in Theme settings > Promotions.');
      return;
    }
    this.embed = SP.createEmbed(root, cfg);
    return Promise.all([fetchFeed(cfg.feedUrl, this.embed), stylesReady(root)]).then(function (loaded) {
      var promos = Feed.normalizeFeed(loaded[0]);
      // HC - live-stock card gates, checked only for this brand's cards - 2026-10-26
      var keys = normalizeKeys(cfg.brandKeys);
      var candidates = normalizeCandidates(cfg.brandCandidates);
      var mine = promos.filter(function (p) { return promoMatches(p, keys, candidates); });
      return typeof self.embed.gate === 'function' ? self.embed.gate(mine) : mine;
    }).then(function (promos) {
      if (!root.isConnected && root.isConnected !== undefined) return;
      self.entries = selectEntries(promos, cfg, self.embed);
      if (!self.entries.length) {
        showEmpty(root, 'No live promotions match “' + label + '”. This section is hidden from shoppers.');
        return;
      }
      self.render();
    }).catch(function () {
      showEmpty(root, 'The promotions feed could not be loaded. This section is hidden from shoppers.');
    });
  };

  // Few promos shouldn't leave most of the row empty. The section's CSS keys
  // on data-bps-layout (desktop only; phones always swipe):
  //   spotlight (1-2) -> wide promo-page "featured" cards filling the row;
  //   grid      (3-4) -> equal columns, no scrolling;
  //   slider    (5+)  -> the scroll-snap rail.
  function layoutFor(count) {
    if (count <= 2) return 'spotlight';
    if (count <= 4) return 'grid';
    return 'slider';
  }

  Slider.prototype.render = function () {
    var self = this;
    var embed = this.embed;
    var layout = layoutFor(this.entries.length);
    // Spotlight cards have room for the subtitle and tags ('regular' density);
    // pc-featured is the promo page's image-left spotlight card.
    if (layout === 'spotlight') embed.engine.cfg.density = 'regular';
    this.root.setAttribute('data-bps-layout', layout);
    this.rail.innerHTML = this.entries.map(function (entry, i) {
      return embed.cardHtml(entry, i, { wide: layout === 'spotlight' });
    }).join('');
    setBackdrops(this.rail);
    this.root.removeAttribute('aria-busy');
    this.root.hidden = false;
    this.bindCards();
    this.bindNav();
  };

  // The backdrop is blurred by 40px, so a 64px-wide copy looks the same as the
  // full banner at about 1% of the bytes. Shopify's CDN resizes with ?width=.
  function backdropUrl(src) {
    if (!/^(https?:)?\/\/cdn\.shopify\.com\//.test(src) && !/^(https?:\/\/[^\/]+)?\/cdn\/shop\//.test(src)) return src;
    var base = src.replace(/([?&])width=\d+(&|$)/, function (m, lead, tail) { return tail ? lead : ''; });
    return base + (base.indexOf('?') === -1 ? '?' : '&') + 'width=64';
  }

  // Each card's text panel sits on its own promo image, blurred and darkened by
  // the section CSS (--bps-bg). The URL is the card's already-sanitized img src;
  // quotes/backslashes are percent-encoded so it can't break out of url("").
  function setBackdrops(rail) {
    Array.prototype.forEach.call(rail.querySelectorAll('.pc'), function (card) {
      var img = card.querySelector('.pc-img img');
      var src = img && img.getAttribute('src');
      if (!src || !card.style || typeof card.style.setProperty !== 'function') return;
      var safe = backdropUrl(src).replace(/["\\\n\r]/g, function (ch) { return encodeURIComponent(ch); });
      card.style.setProperty('--bps-bg', 'url("' + safe + '")');
    });
  }

  // The entry a card's click opens.
  Slider.prototype.entryFor = function (card) {
    return this.entries[parseInt(card.getAttribute('data-sp-index'), 10)];
  };

  Slider.prototype.openFor = function (card) {
    var entry = this.entryFor(card);
    if (entry) this.embed.openModal(entry, card);
  };

  Slider.prototype.bindCards = function () {
    var self = this;
    // The promo page's own guards (createEmbed().cardFor): link CTAs and locked
    // cards never open a modal, and a key on an inner button is left to its click.
    this.rail.addEventListener('click', function (e) {
      var card = self.embed.cardFor(e);
      if (card) self.openFor(card);
    });
    this.rail.addEventListener('keydown', function (e) {
      if (e.key !== 'Enter' && e.key !== ' ') return;
      var card = self.embed.cardFor(e);
      if (!card) return;
      e.preventDefault();
      self.openFor(card);
    });
    // HC - Intent on a card asks for its modal's data before the click, as on
    // the promo page (shoppable-promotions.js, ModalIntent) - 2026-10-06
    if (typeof this.embed.bindPrefetch === 'function') {
      this.embed.bindPrefetch(this.rail, function (card) { return self.entryFor(card); });
    }
  };

  Slider.prototype.step = function () {
    var rail = this.rail;
    var card = rail.querySelector('.pc');
    if (!card) return rail.clientWidth;
    var styles = window.getComputedStyle(rail);
    var gap = parseFloat(styles.columnGap || styles.gap) || 0;
    return card.getBoundingClientRect().width + gap;
  };

  Slider.prototype.bindNav = function () {
    var self = this;
    var rail = this.rail;

    function update() {
      // A few px of slack: snap alignment and sub-pixel card widths rarely land
      // exactly on 0 / max.
      var max = Math.max(0, rail.scrollWidth - rail.clientWidth);
      var scrollable = max > 4;
      if (self.nav) self.nav.hidden = !scrollable;
      if (self.prevBtn) self.prevBtn.disabled = rail.scrollLeft <= 4;
      if (self.nextBtn) self.nextBtn.disabled = rail.scrollLeft >= max - 4;
    }
    function scrollByCards(dir) {
      rail.scrollBy({ left: dir * self.step(), behavior: reduceMotion() ? 'auto' : 'smooth' });
    }

    // Scroll fires many times a frame and update() reads layout; once per frame.
    var raf = window.requestAnimationFrame
      ? function (fn) { window.requestAnimationFrame(fn); }
      : function (fn) { setTimeout(fn, 16); };
    var queued = false;
    function onScroll() {
      if (queued) return;
      queued = true;
      raf(function () { queued = false; update(); });
    }

    if (this.prevBtn) this.prevBtn.addEventListener('click', function () { scrollByCards(-1); });
    if (this.nextBtn) this.nextBtn.addEventListener('click', function () { scrollByCards(1); });
    rail.addEventListener('scroll', onScroll, { passive: true });

    if ('ResizeObserver' in window) {
      var ro = new ResizeObserver(update);
      ro.observe(rail);
      this.cleanup.push(function () { ro.disconnect(); });
    } else {
      window.addEventListener('resize', update);
      this.cleanup.push(function () { window.removeEventListener('resize', update); });
    }
    Array.prototype.forEach.call(rail.querySelectorAll('img'), function (img) {
      if (!img.complete) img.addEventListener('load', update);
    });
    update();
  };

  Slider.prototype.destroy = function () {
    this.cleanup.forEach(function (fn) { fn(); });
    this.cleanup = [];
    // A modal left open by a theme-editor reload would keep its document key
    // handler and the body scroll lock; close it through the engine.
    var engine = this.embed && this.embed.engine;
    if (engine && engine._modalKeyHandler) engine.closeModal($rsm(engine));
    if (this.embed && typeof this.embed.destroy === 'function') this.embed.destroy();
  };

  function $rsm(engine) {
    return engine.modalRoot ? engine.modalRoot.querySelector('.rsm') : null;
  }

  function init(root) {
    if (!root || root.getAttribute('data-bps-init')) return null;
    root.setAttribute('data-bps-init', '1');
    var slider = new Slider(root);
    root._bpsSlider = slider;
    slider.ready = slider.init();
    return slider;
  }

  function initAll(scope) {
    var roots = (scope || document).querySelectorAll('[data-brand-promo-slider]');
    Array.prototype.forEach.call(roots, init);
    if (scope && scope.matches && scope.matches('[data-brand-promo-slider]')) init(scope);
  }

  function destroyIn(scope) {
    if (!scope || !scope.querySelectorAll) return;
    var roots = Array.prototype.slice.call(scope.querySelectorAll('[data-brand-promo-slider]'));
    if (scope.matches && scope.matches('[data-brand-promo-slider]')) roots.push(scope);
    roots.forEach(function (root) {
      if (root._bpsSlider) root._bpsSlider.destroy();
      root._bpsSlider = null;
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(); });
  } else {
    // Next tick, matching shoppable-promotions.js boot(): both modules are
    // assigned before either touches the other.
    setTimeout(function () { initAll(); }, 0);
  }
  document.addEventListener('shopify:section:load', function (e) { initAll(e && e.target); });
  document.addEventListener('shopify:section:unload', function (e) { destroyIn(e && e.target); });

  return {
    init: init,
    initAll: initAll,
    normalizeBrand: normalizeBrand,
    brandMatches: brandMatches,
    promoMatches: promoMatches,
    layoutFor: layoutFor,
    selectEntries: selectEntries
  };
})();
